import {
  ClipboardCheck,
  Pill,
  Bandage,
  GraduationCap,
  Sparkles,
  Info,
  AlertTriangle,
  Droplet,
  Activity,
} from "lucide-react";

const groups = [
  {
    title: "Assessment & Care Planning",
    icon: ClipboardCheck,
    items: [
      "Comprehensive Nursing Assessments",
      "Post Hospital Discharge Reviews",
      "Nursing Care Plans",
      "Care Coordination",
      "Support Coordination",
      "Clinical Liaison with GPs, Specialists and Allied Health",
    ],
  },
  {
    title: "Medication & Clinical Support",
    icon: Pill,
    items: [
      "Medication Management",
      "Medication Administration",
      "Vital Signs Monitoring",
      "Diabetes Monitoring",
      "Chronic Disease Management",
    ],
  },
  {
    title: "Wound & Skin Care",
    icon: Bandage,
    items: [
      "Wound Assessment and Management",
      "Pressure Injury Prevention and Management",
      "Palliative Nursing Support",
      "Complex Nursing Supports",
    ],
  },
  {
    title: "Education & Capacity Building",
    icon: GraduationCap,
    items: [
      "Clinical Health Education",
      "Support Worker Clinical Training",
    ],
  },
  {
    title: "Community & Daily Support",
    icon: Sparkles,
    items: [
      "Community and Social Support",
      "Support with Daily Personal Activities",
    ],
  },
];

const continenceBullets = [
  "NDIS continence assessments",
  "Continence management recommendations",
  "Continence product and consumable recommendations",
  "Reports for participants, support coordinators, plan managers and care teams",
  "Education for participants, families and support workers",
];

const tubeItems = [
  "Urinary catheter care and monitoring",
  "Suprapubic catheter care",
  "Nephrostomy tube care",
  "Gastrostomy or PEG site care",
  "Enteral feeding tube support",
  "Stoma-related nursing support",
  "Surgical drain monitoring",
  "PleurX or indwelling drainage catheter support",
  "Paracentesis or ascitic drain-related care",
  "Drainage procedures prescribed by a treating specialist",
  "Tube and insertion-site assessment",
  "Dressing changes around catheter, drain or tube sites",
  "Monitoring for leakage, blockage, infection or skin breakdown",
  "Participant, family and support worker education",
];

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-20 sm:py-28 bg-[var(--ew-bg)]"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <div className="max-w-3xl">
          <p className="eyebrow">Services Available</p>
          <h2 className="font-heading font-medium mt-3 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.08] text-[var(--ew-text)]">
            Registered Nurse-led care, tailored to each referral.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[var(--ew-text-2)] leading-relaxed">
            Available for{" "}
            <span className="font-semibold text-[var(--ew-accent)]">NDIS</span>,{" "}
            <span className="font-semibold text-[var(--ew-accent)]">
              Support at Home
            </span>
            ,{" "}
            <span className="font-semibold text-[var(--ew-accent)]">TCP</span>,{" "}
            <span className="font-semibold text-[var(--ew-accent)]">DVA</span>{" "}
            and{" "}
            <span className="font-semibold text-[var(--ew-accent)]">CTP</span>{" "}
            pathways across Canberra and nearby NSW. All services are delivered
            within the Registered Nurse&apos;s scope of practice and are subject to
            clinical assessment.
          </p>
        </div>

        {/* Grouped services */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          {groups.map((g, gi) => (
            <article
              key={g.title}
              data-testid={`service-group-${gi}`}
              className="ew-card p-7 sm:p-8 flex flex-col"
            >
              <div className="flex items-center gap-4">
                <div className="icon-badge">
                  <g.icon className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <h3 className="font-heading text-xl sm:text-[1.35rem] font-semibold text-[var(--ew-text)] leading-snug">
                  {g.title}
                </h3>
              </div>

              <ul className="mt-6 grid sm:grid-cols-2 gap-x-5 gap-y-2.5">
                {g.items.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-[var(--ew-text-2)]"
                  >
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[var(--ew-sage)] shrink-0" />
                    <span className="text-sm leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>

        {/* Featured: Continence Assessments */}
        <article
          data-testid="service-continence"
          className="ew-card p-7 sm:p-10 mt-8"
        >
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5">
              <div className="icon-badge">
                <Droplet className="h-5 w-5" strokeWidth={1.75} />
              </div>
              <h3 className="font-heading text-2xl sm:text-3xl font-semibold text-[var(--ew-text)] leading-snug mt-5">
                Continence Assessments
              </h3>
              <p className="mt-4 text-[var(--ew-text-2)] leading-relaxed">
                Comprehensive continence assessments completed by a Registered
                Nurse, including assessment of bladder and bowel needs,
                continence routines, functional considerations, skin integrity
                risks, support requirements and recommendations for suitable
                continence products and ongoing management.
              </p>
            </div>
            <div className="lg:col-span-7">
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[var(--ew-text-muted)]">
                Assessment scope
              </p>
              <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {continenceBullets.map((c) => (
                  <li
                    key={c}
                    className="flex items-start gap-3 text-[var(--ew-text-2)]"
                  >
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[var(--ew-sage)] shrink-0" />
                    <span className="text-sm leading-relaxed">{c}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-5 text-xs text-[var(--ew-text-muted)] italic">
                Funding approval and product funding are not guaranteed;
                outcomes depend on the client&apos;s plan and reviewing decision
                maker.
              </p>
            </div>
          </div>
        </article>

        {/* Featured: Tube, Catheter and Drain Management */}
        <article
          data-testid="service-tube-drain"
          className="ew-card p-7 sm:p-10 mt-6"
        >
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5">
              <div className="icon-badge">
                <Activity className="h-5 w-5" strokeWidth={1.75} />
              </div>
              <h3 className="font-heading text-2xl sm:text-3xl font-semibold text-[var(--ew-text)] leading-snug mt-5">
                Tube, Catheter and Drain Management
              </h3>
              <p className="mt-4 text-[var(--ew-text-2)] leading-relaxed">
                Registered Nurse support for eligible tube, catheter and
                drain-related procedures in the community, based on the
                participant&apos;s clinical needs, the nurse&apos;s scope of practice,
                available clinical information and an established management
                plan.
              </p>
              <p className="mt-4 text-sm text-[var(--ew-text-muted)]">
                Where clinically appropriate and within scope, examples may
                include:
              </p>
            </div>
            <div className="lg:col-span-7">
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {tubeItems.map((c) => (
                  <li
                    key={c}
                    className="flex items-start gap-3 text-[var(--ew-text-2)]"
                  >
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[var(--ew-sage)] shrink-0" />
                    <span className="text-sm leading-relaxed">{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </article>

        {/* Clinical requirements note */}
        <aside
          data-testid="clinical-note"
          className="mt-10 rounded-3xl border border-[var(--ew-blue)]/40 bg-[var(--ew-blue)]/10 p-7 sm:p-9"
        >
          <div className="flex items-start gap-4">
            <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/80 text-[var(--ew-accent)]">
              <Info className="h-5 w-5" strokeWidth={1.75} />
            </span>
            <div>
              <h3 className="font-heading text-lg sm:text-xl font-semibold text-[var(--ew-text)]">
                Clinical Requirements for Procedure-Based Services
              </h3>
              <p className="mt-3 text-[var(--ew-text-2)] leading-relaxed text-sm sm:text-base">
                Procedure-based nursing services, including catheter, tube,
                drain, enteral feeding, stoma and other specialised clinical
                procedures, require a current management plan, care plan or
                written clinical instructions from the participant&apos;s treating
                general practitioner, specialist, hospital team or authorised
                healthcare provider.
              </p>
              <p className="mt-3 text-[var(--ew-text-2)] leading-relaxed text-sm sm:text-base">
                Everwell Community Nursing Pty Ltd will review the referral,
                clinical documentation, required equipment and associated risks
                before confirming service delivery. Acceptance of a referral is
                subject to clinical suitability, staff competency, scope of
                practice and the availability of appropriate medical orders,
                consumables and escalation pathways.
              </p>
            </div>
          </div>

          <div className="mt-5 flex items-start gap-4 rounded-2xl bg-white/70 p-5 border border-[var(--ew-blue)]/30">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--ew-accent)] text-white">
              <AlertTriangle className="h-4 w-4" strokeWidth={2} />
            </span>
            <p className="text-sm text-[var(--ew-text)] leading-relaxed">
              <span className="font-semibold">In urgent or life-threatening situations,</span>{" "}
              clients should call{" "}
              <a
                href="tel:000"
                className="font-semibold text-[var(--ew-accent)] underline underline-offset-2"
              >
                Triple Zero (000)
              </a>{" "}
              or attend the nearest emergency department. Everwell is not an
              emergency response service.
            </p>
          </div>
        </aside>
      </div>
    </section>
  );
};

export default Services;
